import { useEffect, useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { clientsApi } from "@/lib/api";

/**
 * Inline modal to edit a client without leaving the operation form.
 *
 * Props:
 *   - client (object|null) : the client to edit. Modal is open when non-null.
 *   - onClose ()           : close the modal without saving.
 *   - onSaved (client)     : called with the updated client after a successful PUT.
 */
export default function ClientEditDialog({ client, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) {
      setForm(null);
      return;
    }
    setForm({
      nom: client.nom || "",
      prenom: client.prenom || "",
      numero_client: client.numero_client || "",
      telephone: client.telephone || "",
      email: client.email || "",
      adresse1: client.adresse1 || "",
      adresse2: client.adresse2 || "",
      code_postal: client.code_postal || "",
      ville: client.ville || "",
      pays: client.pays || "France",
      observations: client.observations || "",
    });
  }, [client]);

  // Close on Escape
  useEffect(() => {
    if (!client) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [client, onClose]);

  if (!client || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSave = form.nom.trim() && form.adresse1.trim() && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const updated = await clientsApi.update(client.id, form);
      toast.success("Client mis à jour");
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="client-edit-dialog-overlay"
      className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div
        data-testid="client-edit-dialog"
        className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh]"
      >
        <header className="px-5 py-4 border-b border-[var(--vidoc-border)] flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold">Modifier le client</h2>
            <p className="text-xs text-[var(--vidoc-text-secondary)]">
              Vos saisies en cours (photos, signature…) sont conservées.
            </p>
          </div>
          <button
            data-testid="client-edit-close-btn"
            type="button"
            onClick={() => !saving && onClose?.()}
            className="p-2 rounded-lg hover:bg-[var(--vidoc-bg-secondary)] touch-target"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          <Group title="Identité">
            <Row>
              <Field label="Nom" required value={form.nom} onChange={(v) => set("nom", v)} testid="edit-client-nom" />
              <Field label="Prénom" value={form.prenom} onChange={(v) => set("prenom", v)} testid="edit-client-prenom" />
            </Row>
            <Field
              label="N° client externe"
              value={form.numero_client}
              onChange={(v) => set("numero_client", v)}
              testid="edit-client-numero"
            />
          </Group>

          <Group title="Contact">
            <Row>
              <Field label="Téléphone" value={form.telephone} onChange={(v) => set("telephone", v)} testid="edit-client-tel" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="edit-client-email" />
            </Row>
          </Group>

          <Group title="Adresse">
            <Field label="Adresse" required value={form.adresse1} onChange={(v) => set("adresse1", v)} testid="edit-client-adresse1" />
            <Field label="Complément" value={form.adresse2} onChange={(v) => set("adresse2", v)} testid="edit-client-adresse2" />
            <Row>
              <Field label="Code postal" value={form.code_postal} onChange={(v) => set("code_postal", v)} testid="edit-client-cp" />
              <Field label="Ville" value={form.ville} onChange={(v) => set("ville", v)} testid="edit-client-ville" />
              <Field label="Pays" value={form.pays} onChange={(v) => set("pays", v)} testid="edit-client-pays" />
            </Row>
          </Group>

          <Group title="Observations">
            <textarea
              data-testid="edit-client-observations"
              value={form.observations}
              onChange={(e) => set("observations", e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none resize-none"
            />
          </Group>
        </div>

        <footer className="px-5 py-3 border-t border-[var(--vidoc-border)] flex gap-2 sticky bottom-0 bg-white">
          <button
            data-testid="client-edit-cancel-btn"
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] font-medium touch-target disabled:opacity-50 hover:bg-[var(--vidoc-bg-tertiary)]"
          >
            Annuler
          </button>
          <button
            data-testid="client-edit-save-btn"
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </footer>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider font-semibold text-[var(--vidoc-text-secondary)] mb-2">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
}

function Field({ label, required, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[var(--vidoc-text-secondary)] mb-1">
        {label}
        {required && <span className="text-[var(--vidoc-danger)] ml-1">*</span>}
      </div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
      />
    </label>
  );
}
